/**
 * Nova sugestão (v1): colar o link, tocar numa categoria, comentar se quiser.
 * O preview é do servidor (oEmbed YouTube/TikTok) — aqui só o link vai.
 * Criar exige rede: sem ela, a folha explica e não finge.
 */
import { uuidv7, NOTAS_MAX } from '@nv/core';
import { useEffect, useRef, useState } from 'react';

import { useTrip } from '../../trip/TripContext';
import { Sheet } from '../../ui/Sheet';
import { CATEGORIA_ROTULO, CATEGORIAS, type Categoria, useCriarSugestao } from './api';

export function NovaSugestaoSheet({ onFechar }: { onFechar: () => void }) {
  const { tripId } = useTrip();
  const criar = useCriarSugestao(tripId);
  const [url, setUrl] = useState('');
  const [categoria, setCategoria] = useState<Categoria | null>(null);
  const [comentario, setComentario] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);
  useEffect(() => {
    window.setTimeout(() => campo.current?.focus(), 50);
  }, []);

  const pronto = url.trim().length > 0 && categoria !== null && !criar.isPending;

  async function enviar() {
    if (!pronto || !categoria) return;
    setErro(null);
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setErro('Sem rede agora. A sugestão precisa da rede para buscar o preview — tente de novo quando conectar.');
      return;
    }
    try {
      await criar.mutateAsync({ id: uuidv7(), url: url.trim(), category: categoria, comment: comentario.trim() || null });
      onFechar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível guardar a sugestão.');
    }
  }

  return (
    <Sheet titleId="sug-nova-titulo" onFechar={onFechar} testId="sug-nova-sheet">
      <h2 className="sheet__title" id="sug-nova-titulo">Nova sugestão</h2>
      <p className="sheet__text">Cole o link de um vídeo ou página sobre a viagem. Todo mundo do grupo vê.</p>
      <form
        className="sug__form"
        onSubmit={(e) => {
          e.preventDefault();
          void enviar();
        }}
      >
        <label className="field">
          <span className="field__label">Link</span>
          <input
            ref={campo}
            className="field__input"
            type="text"
            inputMode="url"
            autoComplete="off"
            autoCapitalize="off"
            placeholder="https://…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            data-testid="sug-url"
          />
        </label>
        <span className="nv-section-label">Categoria</span>
        <div className="chip-row" role="radiogroup" aria-label="Categoria">
          {CATEGORIAS.map((c) => (
            <button type="button" key={c} role="radio" aria-checked={categoria === c} className={categoria === c ? 'pick pick--on' : 'pick'} onClick={() => setCategoria(c)} data-testid={`sug-cat-${c}`}>
              {CATEGORIA_ROTULO[c]}
            </button>
          ))}
        </div>
        <label className="field">
          <span className="field__label">Comentário (opcional)</span>
          <textarea className="field__input sug__comentario" rows={4} maxLength={NOTAS_MAX} value={comentario} onChange={(e) => setComentario(e.target.value)} placeholder="Por que vale a pena?" data-testid="sug-comentario" />
          <span className="field__contador" aria-live="polite" data-testid="sug-comentario-contador">{comentario.length}/{NOTAS_MAX}</span>
        </label>
        {erro && (
          <p className="auth-note sug__erro" role="alert">
            {erro}
          </p>
        )}
        <button type="submit" className="nv-btn nv-btn--primary nv-btn--block" disabled={!pronto} data-testid="sug-salvar">
          {criar.isPending ? 'Guardando…' : 'Guardar sugestão'}
        </button>
      </form>
    </Sheet>
  );
}
